import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin, Shield, Store, Send, Loader2, X, Building2, MessageCircle, Download, Facebook } from 'lucide-react';
import { useLanguage, LOGO_URL_DARK, API, useCurrency } from '../App';
import { useTheme } from '../App';
import { toast } from '../utils/toastWithSound';
import axios from '../lib/axios';
import { Turnstile } from '@marsidev/react-turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Footer = () => {
  const { t } = useLanguage();
  const { currency } = useCurrency();
  const [showAdvertiseModal, setShowAdvertiseModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [advertiseLoading, setAdvertiseLoading] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [socialLinks, setSocialLinks] = useState({ instagram_url: '', facebook_url: '' });
  
  const [advertiseForm, setAdvertiseForm] = useState({ name: '', email: '', company: '', phone: '', message: '', preferred_placement: '' });
  const [suggestionForm, setSuggestionForm] = useState({ name: '', email: '', message: '' });
  const [advertiseTurnstileToken, setAdvertiseTurnstileToken] = useState(null);
  const [suggestionTurnstileToken, setSuggestionTurnstileToken] = useState(null);

  // Fetch social links on mount
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await axios.get(`${API}/social-links`);
        setSocialLinks(response.data);
      } catch (error) {
        console.log('Using default social links');
      }
    };
    fetchSocialLinks();
  }, []);
  
  const handleAdvertiseSubmit = async (e) => {
    e.preventDefault();
    
    // Verify CAPTCHA
    if (TURNSTILE_SITE_KEY && !advertiseTurnstileToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    
    setAdvertiseLoading(true);
    try {
      await axios.post(`${API}/contact/advertise`, {
        ...advertiseForm,
        turnstile_token: advertiseTurnstileToken
      });
      toast.success('Thank you! We will contact you soon.');
      setShowAdvertiseModal(false);
      setAdvertiseForm({ name: '', email: '', company: '', phone: '', message: '', preferred_placement: '' });
      setAdvertiseTurnstileToken(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit. Please try again.';
      toast.error(errorMsg);
    } finally {
      setAdvertiseLoading(false);
    }
  };
  
  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    
    // Verify CAPTCHA
    if (TURNSTILE_SITE_KEY && !suggestionTurnstileToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    
    setSuggestionLoading(true);
    try {
      await axios.post(`${API}/contact/suggestion`, {
        ...suggestionForm,
        turnstile_token: suggestionTurnstileToken
      });
      toast.success('Thank you for your feedback!');
      setShowSuggestionModal(false);
      setSuggestionForm({ name: '', email: '', message: '' });
      setSuggestionTurnstileToken(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSuggestionLoading(false);
    }
  };
  

  return (
    <footer className="footer py-8 md:py-10" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Advertise CTA - Compact */}
        <div className="mb-8 p-4 rounded-xl footer-advertise-cta" data-testid="advertise-cta-section">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Advertise With Us</h3>
                <p className="text-white/70 text-xs">Reach buyers across the ABC islands</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAdvertiseModal(true)}
              className="px-5 py-2 bg-white text-purple-600 font-semibold rounded-lg hover:bg-white/90 transition-all flex items-center gap-2 text-sm"
              data-testid="advertise-button"
            >
              <MessageCircle className="w-4 h-4" />
              Contact Us
            </button>
          </div>
        </div>
        
        {/* Main Footer Grid - 4 columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-8">
          
          {/* Column 1: Brand & Social */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-3">
              <img src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/tnwfusd0_YBSLogoWhiteNoBack.png" alt="Your Buy Site" className="h-7 w-auto" />
            </Link>
            <p className="text-slate-400 text-xs mb-3">{t('footer_tagline')}</p>
            <div className="flex items-center gap-2">
              <a 
                href={socialLinks.instagram_url || '#'} 
                target={socialLinks.instagram_url ? "_blank" : undefined}
                rel="noopener noreferrer" 
                className={`w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center transition-all ${
                  socialLinks.instagram_url 
                    ? 'text-slate-400 hover:text-white hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 cursor-pointer' 
                    : 'text-slate-600 cursor-default'
                }`}
                onClick={(e) => !socialLinks.instagram_url && e.preventDefault()}
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href={socialLinks.facebook_url || '#'} 
                target={socialLinks.facebook_url ? "_blank" : undefined}
                rel="noopener noreferrer" 
                className={`w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center transition-all ${
                  socialLinks.facebook_url 
                    ? 'text-slate-400 hover:text-white hover:bg-[#1877F2] cursor-pointer' 
                    : 'text-slate-600 cursor-default'
                }`}
                onClick={(e) => !socialLinks.facebook_url && e.preventDefault()}
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
            {/* Sentoo Payment Partner */}
            <div className="mt-3">
              <a 
                href="https://sentoo.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <img 
                  src="/sentoo-logo.png" 
                  alt="Sentoo" 
                  className="h-6 w-auto rounded"
                />
              </a>
            </div>
          </div>

          {/* Column 2: Explore */}
          <div>
            <h4 className="text-white font-semibold mb-3 text-xs uppercase tracking-wider">{t('footer_explore')}</h4>
            <ul className="space-y-1.5">
              <li><Link to="/marketplace" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('marketplace')}</Link></li>
              <li><Link to="/categories" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('categories')}</Link></li>
              <li><Link to="/events" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('footer_events')}</Link></li>
              <li><Link to="/hotspots" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('footer_hotspots')}</Link></li>
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div>
            <h4 className="text-white font-semibold mb-3 text-xs uppercase tracking-wider">{t('footer_resources')}</h4>
            <ul className="space-y-1.5">
              <li><Link to="/about" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('footer_about')}</Link></li>
              <li><Link to="/install-app" className="text-xs text-slate-400 hover:text-teal-400 transition-colors flex items-center gap-1">
                <Download className="w-3 h-3 text-primary" /> {t('footer_install_app')}
              </Link></li>
              <li><Link to="/pricing" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('footer_boost_listing')}</Link></li>
              <li><Link to="/currency-converter" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('footer_currency_converter')}</Link></li>
              <li><Link to="/faq" className="text-xs text-slate-400 hover:text-teal-400 transition-colors">{t('footer_faq')}</Link></li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="text-white font-semibold mb-3 text-xs uppercase tracking-wider">{t('footer_contact')}</h4>
            <ul className="space-y-1.5 mb-3">
              <li className="flex items-center gap-2 text-xs text-slate-400">
                <MapPin className="w-3 h-3 text-teal-400 flex-shrink-0" />
                Curaçao
              </li>
              <li className="flex items-center gap-2 text-xs text-slate-400">
                <Mail className="w-3 h-3 text-teal-400 flex-shrink-0" />
                <a href="mailto:info@yourbuysite.com" className="hover:text-teal-400 transition-colors">
                  info@yourbuysite.com
                </a>
              </li>
            </ul>
            <button 
              onClick={() => setShowSuggestionModal(true)}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white transition-all flex items-center gap-1.5"
            >
              <Send className="w-3 h-3" /> {t('footer_feedback')}
            </button>
          </div>
        </div>
        
        {/* Safety Tips - Compact Single Row */}
        <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
          <div className="flex items-center gap-2 flex-wrap">
            <Shield className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-white font-medium text-xs">{t('safety_tips')}:</span>
            <span className="text-xs text-slate-300">{t('meet_public')} • {t('check_item')} • {t('no_advance')}</span>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-4 border-t border-slate-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-slate-500 text-xs">© {new Date().getFullYear()} Your Buy Site • Curaçao</p>
            <div className="flex items-center gap-3 text-xs">
              <Link to="/terms" className="text-slate-500 hover:text-teal-400 transition-colors">{t('footer_terms')}</Link>
              <Link to="/privacy" className="text-slate-500 hover:text-teal-400 transition-colors">{t('footer_privacy')}</Link>
            </div>
          </div>
          
          {/* Paradise FM - Small */}
          <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-slate-800/50">
            <span className="text-slate-600 text-[10px]">Powered by</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/37hyacoq_Ontwerp%20zonder%20titel-3.png" 
              alt="Paradise FM 103.1" 
              className="h-8 opacity-60 hover:opacity-100 transition-opacity"
            />
          </div>
          
          <p className="mt-2 text-[10px] text-slate-600 text-center">
            Your Buy Site is a platform only. All transactions are at your own risk. <Link to="/terms" className="text-teal-500 hover:underline">Full Terms</Link>
          </p>
        </div>
      </div>
      
      {/* Advertise Modal */}
      {showAdvertiseModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowAdvertiseModal(false)}>
          <div className="bg-slate-900 rounded-2xl p-5 max-w-md w-full border border-slate-700 my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-400" /> Advertise With Us
              </h3>
              <button onClick={() => setShowAdvertiseModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            
            {/* Info text instead of pricing */}
            <p className="mb-4 text-sm text-slate-400">
              Interested in advertising on our platform? Fill out the form below and we'll contact you to discuss options and pricing.
            </p>
            
            <form onSubmit={handleAdvertiseSubmit} className="space-y-3">
              <input type="text" required value={advertiseForm.name} onChange={e => setAdvertiseForm({...advertiseForm, name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:border-purple-500 outline-none" placeholder="Your Name *" />
              <input type="email" required value={advertiseForm.email} onChange={e => setAdvertiseForm({...advertiseForm, email: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:border-purple-500 outline-none" placeholder="Email *" />
              <div className="grid grid-cols-2 gap-3">
                <input type="text" value={advertiseForm.company} onChange={e => setAdvertiseForm({...advertiseForm, company: e.target.value})}
                  className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 outline-none" placeholder="Company" />
                <input type="tel" value={advertiseForm.phone} onChange={e => setAdvertiseForm({...advertiseForm, phone: e.target.value})}
                  className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 outline-none" placeholder="Phone" />
              </div>
              <select required value={advertiseForm.preferred_placement} onChange={e => setAdvertiseForm({...advertiseForm, preferred_placement: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-purple-500 outline-none">
                <option value="">Where would you like to advertise? *</option>
                <option value="homepage_top">Homepage Top Banner</option>
                <option value="homepage_middle">Homepage Middle (2-Up)</option>
                <option value="homepage_bottom">Homepage Bottom Banner</option>
                <option value="category_landing">Category Landing Pages</option>
                <option value="in_grid_ads">In-Grid Ads (Marketplace)</option>
                <option value="multiple">Multiple Locations</option>
                <option value="not_sure">Not Sure Yet</option>
              </select>
              <textarea required value={advertiseForm.message} onChange={e => setAdvertiseForm({...advertiseForm, message: e.target.value})} rows={2}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:border-purple-500 outline-none resize-none" placeholder="Message *" />
              
              {/* Turnstile CAPTCHA */}
              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setAdvertiseTurnstileToken(token)}
                    onError={() => setAdvertiseTurnstileToken(null)}
                    onExpire={() => setAdvertiseTurnstileToken(null)}
                    options={{ theme: 'dark', size: 'compact' }}
                  />
                </div>
              )}
              
              <button type="submit" disabled={advertiseLoading || (TURNSTILE_SITE_KEY && !advertiseTurnstileToken)}
                className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                {advertiseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {advertiseLoading ? 'Sending...' : 'Send Inquiry'}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Suggestion Modal */}
      {showSuggestionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150] flex items-center justify-center p-4" onClick={() => setShowSuggestionModal(false)}>
          <div className="bg-slate-900 rounded-2xl p-5 max-w-sm w-full border border-slate-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-teal-400" /> Share Your Ideas
              </h3>
              <button onClick={() => setShowSuggestionModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSuggestionSubmit} className="space-y-3">
              <input type="text" value={suggestionForm.name} onChange={e => setSuggestionForm({...suggestionForm, name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 outline-none" placeholder="Name (optional)" />
              <input type="email" required value={suggestionForm.email} onChange={e => setSuggestionForm({...suggestionForm, email: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:border-teal-500 outline-none" placeholder="Email *" />
              <textarea required value={suggestionForm.message} onChange={e => setSuggestionForm({...suggestionForm, message: e.target.value})} rows={3}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:border-teal-500 outline-none resize-none" placeholder="Your suggestion *" />
              
              {/* Turnstile CAPTCHA */}
              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setSuggestionTurnstileToken(token)}
                    onError={() => setSuggestionTurnstileToken(null)}
                    onExpire={() => setSuggestionTurnstileToken(null)}
                    options={{ theme: 'dark', size: 'compact' }}
                  />
                </div>
              )}
              
              <button type="submit" disabled={suggestionLoading || (TURNSTILE_SITE_KEY && !suggestionTurnstileToken)}
                className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                {suggestionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {suggestionLoading ? 'Sending....' : 'Send Suggestion'}
              </button>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
